import { useState } from "react";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { registerUser } from "../api/auth";
import { getApiError } from "../api/errors";
import AppLogo from "../components/AppLogo";
import LanguageSelector from "../components/LanguageSelector";
import "../i18n";

function passwordScore(value) {
  return [
    value.length >= 10,
    /[a-z]/.test(value),
    /[A-Z]/.test(value),
    /\d/.test(value),
  ].filter(Boolean).length;
}

const initialForm = {
  username: "",
  email: "",
  password: "",
  workspace_name: "",
  store_name: "Main Store",
  business_type: "",
  contact_phone: "",
  onboarding_method: "manual",
};

const onboardingOptions = [
  { value: "manual", label: "auth.manualEntry", helper: "auth.manualHelp" },
  { value: "excel", label: "auth.excelImport", helper: "auth.excelHelp" },
  { value: "pos_connector", label: "auth.posConnector", helper: "auth.posHelp" },
];

export default function Register() {
  const { t } = useTranslation();
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const score = passwordScore(form.password);

  const handleChange = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await registerUser({
        username: form.username.trim(),
        email: form.email.trim(),
        password: form.password,
        workspace_name: form.workspace_name.trim(),
        store_name: form.store_name.trim(),
        business_type: form.business_type.trim(),
        contact_phone: form.contact_phone.trim() || null,
        onboarding_method: form.onboarding_method,
      });
      localStorage.setItem("auth_error", t("auth.applicationSubmitted"));
      navigate("/login", { replace: true });
    } catch (requestError) {
      setError(getApiError(requestError, t("auth.applicationError")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        p: { xs: 1.5, sm: 3 },
        background:
          "radial-gradient(circle at 12% 10%, rgba(59,130,246,.22), transparent 28%), radial-gradient(circle at 88% 15%, rgba(124,58,237,.2), transparent 30%), #020617",
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 1120, borderRadius: 5, overflow: "hidden", boxShadow: "0 36px 100px rgba(0,0,0,.42)" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "0.72fr 1.28fr" } }}>
          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              flexDirection: "column",
              justifyContent: "space-between",
              p: 5,
              color: "white",
              background: "linear-gradient(155deg, #0f172a 0%, #1e3a8a 55%, #4c1d95 100%)",
            }}
          >
            <AppLogo light />
            <Box>
              <Typography variant="h3" sx={{ color: "white", fontSize: "2.35rem", lineHeight: 1.12 }}>
                {t("auth.registerHeadline")}
              </Typography>
              <Typography sx={{ color: "#cbd5e1", mt: 2, lineHeight: 1.75 }}>
                {t("auth.registerIntro")}
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 3.5 }}>
                {["auth.registerBenefitSecure", "auth.registerBenefitFlexible", "auth.registerBenefitStaff"].map((item) => (
                  <Stack direction="row" spacing={1.2} alignItems="center" key={item}>
                    <CheckCircleRoundedIcon sx={{ color: "#4ade80", fontSize: 20 }} />
                    <Typography sx={{ color: "#e2e8f0", fontWeight: 650 }}>{t(item)}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
              {t("auth.registerReview")}
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 3, sm: 4.5, md: 5.5 } }}>
            <Box sx={{ display: { xs: "block", lg: "none" }, mb: 3 }}>
              <AppLogo />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}><LanguageSelector /></Box>
            <Typography variant="h4">{t("auth.registerTitle")}</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.75, mb: 3 }}>
              {t("auth.registerSubtitle")}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}

            <Box component="form" onSubmit={handleSubmit}>
              <Typography variant="overline" color="primary.main" fontWeight={900}>
                {t("auth.businessDetails")}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 0.75 }}>
                <TextField fullWidth required name="workspace_name" label={t("auth.businessName")} value={form.workspace_name} onChange={handleChange} inputProps={{ minLength: 2, maxLength: 150 }} />
                <TextField fullWidth required name="store_name" label={t("auth.mainStoreName")} value={form.store_name} onChange={handleChange} inputProps={{ minLength: 2, maxLength: 150 }} />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2 }}>
                <TextField fullWidth required name="business_type" label={t("auth.businessType")} placeholder={t("auth.businessExample")} value={form.business_type} onChange={handleChange} inputProps={{ minLength: 2, maxLength: 80 }} />
                <TextField fullWidth name="contact_phone" label={t("auth.businessPhone")} value={form.contact_phone} onChange={handleChange} inputProps={{ minLength: 6, maxLength: 30 }} />
              </Stack>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="onboarding-method-label">{t("auth.addProducts")}</InputLabel>
                <Select
                  labelId="onboarding-method-label"
                  name="onboarding_method"
                  value={form.onboarding_method}
                  label={t("auth.addProducts")}
                  onChange={handleChange}
                >
                  {onboardingOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box>
                        <Typography fontWeight={800}>{t(option.label)}</Typography>
                        <Typography variant="caption" color="text.secondary">{t(option.helper)}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="overline" color="primary.main" fontWeight={900} display="block" sx={{ mt: 3 }}>
                {t("auth.ownerAccount")}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 0.75 }}>
                <TextField fullWidth required name="username" label={t("auth.username")} value={form.username} onChange={handleChange} inputProps={{ minLength: 3, maxLength: 50, pattern: "[A-Za-z0-9_-]+" }} />
                <TextField fullWidth required name="email" label={t("auth.email")} type="email" value={form.email} onChange={handleChange} autoComplete="email" />
              </Stack>

              <TextField
                fullWidth
                required
                name="password"
                label={t("auth.password")}
                type="password"
                value={form.password}
                onChange={handleChange}
                margin="normal"
                autoComplete="new-password"
                helperText={t("auth.registerPasswordHelp")}
              />
              {form.password && (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={score * 25}
                    color={score === 4 ? "success" : "primary"}
                    sx={{ height: 7, borderRadius: 999, bgcolor: "#e2e8f0" }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.6, display: "block" }}>
                    {t("auth.passwordStrength", { level: t(score === 4 ? "auth.strong" : score >= 2 ? "auth.keepGoing" : "auth.weak") })}
                  </Typography>
                </Box>
              )}

              <Button fullWidth type="submit" variant="contained" disabled={submitting || score < 4} sx={{ mt: 2.5, py: 1.2 }}>
                {submitting ? <CircularProgress size={23} color="inherit" /> : t("auth.submitApplication")}
              </Button>
            </Box>

            <Typography mt={2.75} textAlign="center" color="text.secondary">
              {t("auth.alreadyRegistered")}{" "}
              <Link to="/login" style={{ color: "#2563eb", fontWeight: 850 }}>{t("auth.signIn")}</Link>
            </Typography>
          </CardContent>
        </Box>
      </Card>
    </Box>
  );
}
